import { useMemo } from "react"
import { Swatch } from "@/components/chart/ChartTooltip"
import { OhlcHoverCard, type HoverAnchor } from "@/components/chart/OhlcHoverCard"
import { useChartDrawing } from "@/hooks/useChartDrawing"
import { CHART_NAVIGATION_HINT, useCanvasFrame, useCanvasSize, useChartViewport } from "@/hooks/useChartViewport"
import { useChartTheme } from "@/lib/chart/ChartThemeContext"
import { formatNumber, formatPrice, formatTimeLabel, percentChange } from "@/lib/chart/format"
import { INDICATOR_LABELS } from "@/lib/chart/indicators"
import { buildFinancialChartLayout, candleWidthFor, indicatorValueAt, renderFinancialChart } from "@/lib/chart/render"
import { aggregateWeeklyBars, visibleBarCount, type ChartInterval, type ChartRange } from "@/lib/chart/timeframes"
import { cn } from "@/lib/cn"
import type { ChartOverlay, ChartToolMode } from "@/types/chartOverlays"
import type { FuturesBar, FuturesChartPreferences } from "@/types/futuresChart"

interface Props {
  bars: FuturesBar[]
  preferences: FuturesChartPreferences
  interval: ChartInterval
  range: ChartRange
  overlays?: ChartOverlay[]
  toolMode?: ChartToolMode
  drawingEnabled?: boolean
  onPlaceDrawing?: (price: number, originTime: string | null) => void
  onMoveDrawing?: (overlayId: string, price: number) => void
  onSelectDrawing?: (overlayId: string | null) => void
}

export function ChartSurface({
  bars,
  preferences,
  interval,
  range,
  overlays = [],
  toolMode = "pan",
  drawingEnabled = false,
  onPlaceDrawing,
  onMoveDrawing,
  onSelectDrawing,
}: Props) {
  const theme = useChartTheme()
  const displayBars = useMemo(
    () => (interval === "weekly" ? aggregateWeeklyBars(bars) : bars),
    [bars, interval],
  )
  const defaultWindow = useMemo(() => visibleBarCount(displayBars, range), [displayBars, range])
  const resetKey = useMemo(() => ({}), [displayBars, range])
  const {
    containerRef,
    canvasRef,
    size,
    viewport,
    hoverIndex,
    hoverY,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
  } = useChartViewport({
    length: displayBars.length,
    defaultWindow,
    minHeight: 420,
    maxHeight: 620,
    heightRatio: 0.62,
    resetKey,
  })

  const drawing = useChartDrawing({
    bars: displayBars,
    preferences,
    viewport,
    size,
    canvasRef,
    overlays,
    toolMode,
    enabled: drawingEnabled,
    onPlaceDrawing: onPlaceDrawing ?? (() => undefined),
    onMoveDrawing: onMoveDrawing ?? (() => undefined),
    onSelectDrawing: onSelectDrawing ?? (() => undefined),
  })

  const renderedOverlays = useMemo(
    () =>
      overlays.map((overlay) => ({
        ...overlay,
        price: drawing.dragPreview?.id === overlay.id ? drawing.dragPreview.price : overlay.price,
        selected: overlay.id === drawing.selectedOverlayId,
      })),
    [drawing.dragPreview, drawing.selectedOverlayId, overlays],
  )

  useCanvasSize(canvasRef, size)
  useCanvasFrame(
    canvasRef,
    size,
    (ctx) =>
      renderFinancialChart({
        ctx,
        width: size.width,
        height: size.height,
        bars: displayBars,
        start: viewport.start,
        end: viewport.end,
        preferences,
        hoverIndex,
        hoverY,
        overlays: renderedOverlays,
        theme,
      }),
    [displayBars, hoverIndex, hoverY, preferences, renderedOverlays, theme, viewport],
  )

  const hoveredBar = hoverIndex === null ? null : (displayBars[hoverIndex] ?? null)

  // Where the hovered bar sits on screen, so the OHLC card can pop up right beside it.
  const hoverAnchor = useMemo((): HoverAnchor | null => {
    if (!hoveredBar || hoverIndex === null || hoverIndex < viewport.start || hoverIndex >= viewport.end) return null
    const layout = buildFinancialChartLayout({
      width: size.width,
      height: size.height,
      bars: displayBars,
      start: viewport.start,
      end: viewport.end,
      preferences,
    })
    if (!layout) return null
    const { main, priceScale } = layout
    return {
      x: layout.x(hoverIndex - viewport.start),
      top: priceScale.map(hoveredBar.high),
      bottom: priceScale.map(hoveredBar.low),
      halfWidth: candleWidthFor(main.width, layout.visible.length) / 2,
      plot: { left: main.left, right: main.right, top: main.top, bottom: layout.plotBottom },
    }
  }, [displayBars, hoverIndex, hoveredBar, preferences, size.height, size.width, viewport.end, viewport.start])
  const hoveredChange =
    hoveredBar && hoverIndex !== null
      ? percentChange(hoveredBar.close, displayBars[hoverIndex - 1]?.close ?? hoveredBar.open)
      : null

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (drawing.handleDrawingPointerDown(event)) return
    if (toolMode === "pan") handlePointerDown(event)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (drawing.handleDrawingPointerMove(event)) return
    handlePointerMove(event)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    drawing.handleDrawingPointerUp()
    handlePointerUp()
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative min-h-[420px] w-full touch-pan-y overflow-hidden overscroll-none rounded-b-2xl",
        theme.classes.surface,
      )}
    >
      <canvas
        ref={canvasRef}
        className={`block touch-pan-y ${toolMode === "pan" ? "cursor-crosshair" : "cursor-cell"}`}
        aria-label="Interactive price chart"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={handlePointerLeave}
      />
      {hoveredBar && hoverAnchor && hoverIndex !== null && (
        <OhlcHoverCard
          theme={theme}
          anchor={hoverAnchor}
          chartWidth={size.width}
          title={formatTimeLabel(hoveredBar.time)}
          badge={
            hoveredChange !== null && (
              <span className="tabular-nums" style={{ color: hoveredChange >= 0 ? theme.up : theme.down }}>
                {hoveredChange >= 0 ? "+" : ""}
                {hoveredChange.toFixed(2)}%
              </span>
            )
          }
          ohlc={[
            { label: "O", value: formatPrice(hoveredBar.open) },
            { label: "H", value: formatPrice(hoveredBar.high) },
            { label: "L", value: formatPrice(hoveredBar.low) },
            {
              label: "C",
              value: formatPrice(hoveredBar.close),
              color: hoveredBar.close >= hoveredBar.open ? theme.up : theme.down,
            },
          ]}
          rows={[
            ...preferences.indicators.map((key) => {
              const value = indicatorValueAt(displayBars, key, hoverIndex)
              return {
                key,
                label: (
                  <>
                    <Swatch color={theme.indicators[key]} />
                    {INDICATOR_LABELS[key]}
                  </>
                ),
                value: value === null ? "—" : formatPrice(value),
              }
            }),
            ...(hoveredBar.volume != null
              ? [{ key: "volume", label: "Volume", value: formatNumber(hoveredBar.volume, { compact: false }) }]
              : []),
            ...(hoveredBar.open_interest != null
              ? [{ key: "oi", label: "OI", value: formatNumber(hoveredBar.open_interest, { compact: false }) }]
              : []),
          ]}
        />
      )}
      <div className={cn("px-4 pb-2 text-[10px]", theme.classes.hint)}>
        {toolMode === "pan" ? CHART_NAVIGATION_HINT : "Click chart to place level"}
      </div>
    </div>
  )
}
